import type { PlayerIndex } from '@unmatched/engine';
import { content, portraitCard } from '@unmatched/content';

import { BOT_LEVEL_LABEL, type MatchView, type PublicState } from '../api.js';
import type { Connection } from '../game/useServerGame.js';
import { CardStyleToggle } from './CardStyleToggle.js';
import { Logo } from './Logo.js';
import { cardImageUrl } from '../cardImage.js';

/**
 * Hlavička zápasu a pás hráčov (docs/design/5-hra.png).
 *
 * Hlavička nesie identitu stola a nástroje, pás pod ňou to, čo treba mať
 * stále na očiach: kto hrá za koho, zdravie všetkých figúrok, karty v ruke,
 * balíčku a odhode — a v strede, čí je ťah. Ovládanie ťahu je dole v doku,
 * tam, kam sa hráč pozerá, keď hrá.
 */

const CONN_LABEL: Record<Connection, string> = {
  online: 'live',
  connecting: 'pripájam',
  offline: 'offline',
};

interface HeaderProps {
  state: PublicState;
  view: MatchView;
  connection: Connection;
  nameOf: (player: number) => string;
  onLeave: () => void;
}

export function GameHeader({ state, view, connection, nameOf, onLeave }: HeaderProps) {
  const title = state.players.map((p) => nameOf(p.index)).join(' vs ');
  return (
    <header className="gameHeader">
      <button type="button" className="appHeader__brand" onClick={onLeave} title="Späť do klubovne">
        <Logo compact />
      </button>
      <span className="tableChip">
        <span className="dot dot--primary" /> Stôl {view.id.slice(0, 4).toUpperCase()}: {title}
      </span>
      <span className={`roundChip ${state.combat ? 'is-combat' : ''}`}>
        {state.phase === 'ended'
          ? 'Zápas skončil'
          : `Ťah ${state.turn.number} · ${
              state.combat
                ? 'súbojový clash'
                : state.pending.length
                  ? 'efekt karty'
                  : state.maneuver
                    ? 'manéver'
                    : 'akcie'
            }`}
      </span>

      <div className="gameHeader__right">
        {view.mode === 'online' ? (
          <span className={`connChip connChip--${connection}`}>
            <span className="dot" /> {CONN_LABEL[connection]}
          </span>
        ) : view.mode === 'solo' ? (
          <span className="connChip">proti botovi</span>
        ) : (
          <span className="connChip">hot-seat</span>
        )}
        <CardStyleToggle />
        <button type="button" className="btn btn--danger btn--sm" onClick={onLeave}>
          Opustiť stôl
        </button>
      </div>
    </header>
  );
}

interface StripProps {
  state: PublicState;
  view: MatchView;
  nameOf: (player: number) => string;
}

export function PlayerStrip({ state, view, nameOf }: StripProps) {
  const players = state.players;
  // V duele: ja vľavo, súper vpravo, ťah v strede — ako v mocku. Pri 3–4
  // hráčoch ide stred za prvého hráča a ostatní za ním.
  const me = players.find((p) => view.yourSlots.includes(p.index) && view.mode !== 'hotseat');
  const ordered = me ? [me, ...players.filter((p) => p !== me)] : players;
  const left = ordered.slice(0, 1);
  const right = ordered.slice(1);

  function turnLine() {
    if (state.phase === 'ended') {
      const label =
        state.format === 'teams'
          ? `tím ${(state.winner ?? 0) === 0 ? 'A' : 'B'}`
          : nameOf(state.winner ?? 0);
      return (
        <>
          <b>Koniec</b>
          <span>víťaz: {label}</span>
        </>
      );
    }
    const who = state.turn.player;
    return (
      <>
        <b>
          Ťah {state.turn.number} ·{' '}
          {/* Na ťahu je majiteľ ťahu — nie ten, kto sa práve bráni. */}
          {view.yourSlots.includes(who) && view.mode !== 'hotseat' ? 'tvoj ťah' : 'na ťahu'}
        </b>
        <span style={{ color: `var(--p${who})` }}>{nameOf(who)}</span>
      </>
    );
  }

  return (
    <div className={`playerStrip ${players.length > 2 ? 'is-crowded' : ''}`}>
      {left.map((player) => (
        <PlayerCard
          key={player.index}
          state={state}
          view={view}
          index={player.index}
          name={nameOf(player.index)}
        />
      ))}
      <div className="turnChip">
        <span className={`turnChip__dot ${state.phase === 'ended' ? '' : 'is-live'}`} />
        <span className="turnChip__text">{turnLine()}</span>
      </div>
      {right.map((player) => (
        <PlayerCard
          key={player.index}
          state={state}
          view={view}
          index={player.index}
          name={nameOf(player.index)}
          mirrored={players.length === 2}
        />
      ))}
    </div>
  );
}

/**
 * Hráči po stranách dosky: prvý (ja) vľavo, súper presne oproti vpravo.
 * Pri 3–4 hráčoch sa striedajú — 1. a 3. vľavo, 2. a 4. vpravo; v 2v2 tak
 * sedia spoluhráči na jednej strane (tím = slot % 2). Ten istý obsah ako
 * `PlayerStrip`, len na výšku, aby doska dostala celú šírku medzi nimi.
 */
export function SidePlayers({
  state,
  view,
  nameOf,
  side,
}: StripProps & { side: 'left' | 'right' }) {
  const players = state.players;
  const me = players.find((p) => view.yourSlots.includes(p.index) && view.mode !== 'hotseat');
  const ordered = me ? [me, ...players.filter((p) => p !== me)] : players;
  const shown = ordered.filter((_, i) => (i % 2 === 0) === (side === 'left'));
  return (
    <>
      {shown.map((player) => (
        <PlayerCard
          key={player.index}
          state={state}
          view={view}
          index={player.index}
          name={nameOf(player.index)}
          mirrored={side === 'right'}
          vertical
          compact={players.length > 2}
        />
      ))}
    </>
  );
}

function PlayerCard({
  state,
  view,
  index,
  name,
  mirrored,
  vertical,
  compact,
}: {
  state: PublicState;
  view: MatchView;
  index: PlayerIndex;
  name: string;
  mirrored?: boolean;
  /** Na výšku, v stĺpci vedľa dosky. */
  vertical?: boolean;
  /** Viac hráčov pod sebou — menší portrét. */
  compact?: boolean;
}) {
  const player = state.players.find((p) => p.index === index)!;
  const hero = content.heroes[player.heroId];
  const seat = view.seats.find((s) => s.slot === index);
  const fighters = state.fighters.filter((f) => f.owner === index);
  // Hrdina mimo dosky (Vanish) nie je porazený — ukážeme ho so štítkom.
  const away = (state.limbo ?? []).find((f) => f.owner === index && f.kind === 'hero');
  const heroFighter = fighters.find((f) => f.kind === 'hero') ?? away;
  const sidekicks = fighters.filter((f) => f.kind === 'sidekick');
  const onTurn = state.phase !== 'ended' && state.turn.player === index;

  return (
    <div
      className={[
        'fighterCard',
        `fighterCard--p${index}`,
        onTurn ? 'is-turn' : '',
        player.eliminated ? 'is-out' : '',
        mirrored ? 'is-mirrored' : '',
        vertical ? 'fighterCard--vertical' : '',
        compact ? 'is-compact' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Portrait defId={player.heroId} fallback={hero?.icon} label={hero?.name} />

      <div className="fighterCard__body">
        <div className="fighterCard__name">
          <span className="seatTag seatTag--mini">P{index + 1}</span>
          {hero?.name ?? player.heroId}
          {seat?.isYou && view.mode !== 'hotseat' ? (
            <span className="fighterCard__you">ty</span>
          ) : null}
          {view.mode === 'online' && seat?.nick && !seat.isYou ? (
            <span className="fighterCard__nick">{name}</span>
          ) : null}
          {seat?.isBot ? (
            <span className="fighterCard__nick">
              bot{seat.botLevel ? ` · ${BOT_LEVEL_LABEL[seat.botLevel].toLowerCase()}` : ''}
            </span>
          ) : null}
          {state.format === 'teams' ? (
            <span className="fighterCard__team">tím {player.team === 0 ? 'A' : 'B'}</span>
          ) : null}
          {player.heroId === 'ciri' ? (
            // Source karty v odhode rozhodujú o sile Ciriiných kariet — nech sa nemusia počítať.
            <span className="fighterCard__tag" title="Source karty v odhode">
              🔵 {player.discard.filter((id) => content.cards[state.cards[id]?.defId ?? '']?.name.includes('🔵')).length}
            </span>
          ) : null}
          {player.heroId === 'alice' && player.form ? (
            <span className="fighterCard__tag fighterCard__tag--hot" title="Veľká: +2 k útoku · Malá: +1 k obrane">
              {player.form === 'small' ? 'Malá · +1 obrana' : 'Veľká · +2 útok'}
            </span>
          ) : null}
          {player.heroId === 'beowulf' ? (
            <span className="fighterCard__tag fighterCard__tag--hot" title="Rage: najviac 3, +1 za utŕžené poškodenie">
              Rage {player.counters?.rage ?? 0}/3
            </span>
          ) : null}
          {player.heroId === 'jekyll-hyde' ? (
            <span
              className="fighterCard__tag fighterCard__tag--hot"
              title="Jekyll hrá karty DR. JEKYLL, Hyde karty MR. HYDE; Hyde dostane po manévri 1 poškodenie"
            >
              {player.form === 'hyde' ? 'Mr. Hyde' : 'Dr. Jekyll'}
            </span>
          ) : null}
          {player.heroId === 'hamlet' && player.form ? (
            <span
              className="fighterCard__tag fighterCard__tag--hot"
              title="TO BE: pri manévri o kartu viac · NOT TO BE: Hamlet útočí +2"
            >
              {player.form === 'notbe' ? 'Not to be · +2 útok' : 'To be · +1 karta'}
            </span>
          ) : null}
          {player.heroId === 'sun-wukong' ? (
            <span className="fighterCard__tag" title="Klony mimo dosky — Sun Wukong ich vyvoláva">
              Klony v zálohe {(state.defeated ?? []).filter((f) => f.owner === player.index && f.defId === 'clones').length}
            </span>
          ) : null}
          {player.heroId === 'ghost-rider' ? (
            <span className="fighterCard__tag fighterCard__tag--hot" title="Hellfire — míňa sa na efekty kariet a jazdu pri manévri">
              Hellfire {player.counters?.hellfire ?? 0}
            </span>
          ) : null}
          {player.heroId === 'willow' && player.form === 'dark' ? (
            <span className="fighterCard__tag fighterCard__tag--hot" title="Platia efekty (DARK); na konci ťahu vedľa Tary sa vráti">
              Dark Willow
            </span>
          ) : null}
          {player.heroId === 'nikola-tesla' ? (
            <span className="fighterCard__tag fighterCard__tag--hot" title="Nabité cievky (najviac 2)">
              Cievky {player.counters?.coils ?? 0}/2
            </span>
          ) : null}
          {(player.suit ?? []).length > 0 ? (
            <span className="fighterCard__tag" title="Vibranium Suit — súperove karty, len na boost">
              Suit {player.suit!.length}
            </span>
          ) : null}
          {player.heroId === 'eredin' && !player.eliminated && !fighters.some((f) => f.defId === 'red-riders') ? (
            <span className="fighterCard__tag fighterCard__tag--hot">Enraged</span>
          ) : null}
          {player.ongoing ? (
            <span className="fighterCard__tag" title="Trvalá schéma — leží pred hráčom">
              ⟳ {content.cards[state.cards[player.ongoing]?.defId ?? '']?.name ?? 'schéma'}
            </span>
          ) : null}
          {hero ? (
            <span className="fighterCard__range">
              {hero.attack === 'melee' ? 'blízko' : 'diaľka'}
            </span>
          ) : null}
        </div>

        <div className="fighterCard__fighters">
          {player.eliminated ? (
            <span className="hp">mimo hry</span>
          ) : (
            <>
              {heroFighter ? (
                <HpBar
                  name={away ? `${heroFighter.name} · mimo dosky` : heroFighter.name}
                  hp={heroFighter.hp}
                  max={heroFighter.maxHp}
                  hero
                />
              ) : null}
              {sidekicks.length > 0 ? (
                <div className="fighterCard__minions">
                  {sidekicks.map((sidekick) => (
                    <HpBar
                      key={sidekick.id}
                      name={sidekick.name}
                      hp={sidekick.hp}
                      max={sidekick.maxHp}
                      mini
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="fighterCard__deck">
        <span>
          <small>Ruka</small>
          <b>{player.handSize}</b>
        </span>
        <span className={player.deckSize <= 3 ? 'is-low' : ''}>
          <small>Balík</small>
          <b>{player.deckSize}</b>
        </span>
        <span>
          <small>Odhod</small>
          <b>{player.discard.length}</b>
        </span>
      </div>
      {onTurn ? <span className="fighterCard__turn">na ťahu</span> : null}
    </div>
  );
}

/**
 * Portrét postavy. Zdroj obrázky hrdinov nemá, preto sa berie art jej vlastnej
 * karty a oreže na hornú časť — tam je na kartách Unmatched nakreslená postava.
 * Keď karta s obrázkom neexistuje (testoví hrdinovia), zostane ikona.
 */
function Portrait({
  defId,
  fallback,
  label,
}: {
  defId: string;
  fallback?: string;
  label?: string;
}) {
  const card = portraitCard(defId);
  return (
    <span className="fighterCard__portrait">
      {card ? (
        <img
          className="fighterCard__art"
          src={cardImageUrl(card)}
          alt=""
          loading="lazy"
          draggable={false}
        />
      ) : fallback ? (
        <img src={fallback} alt="" />
      ) : (
        (label?.slice(0, 1) ?? '?')
      )}
    </span>
  );
}

/**
 * Prúžok zdravia. Číslo samo o sebe nepovie, ako blízko je figúrka ku koncu —
 * pomer áno, preto prúžok. Pri tretine a menej zčervenie, nech to praští do očí.
 */
export function HpBar({
  name,
  hp,
  max,
  hero,
  mini,
}: {
  name: string;
  hp: number;
  max: number;
  hero?: boolean;
  mini?: boolean;
}) {
  const ratio = max > 0 ? Math.max(0, Math.min(1, hp / max)) : 0;
  const className = [
    'hpbar',
    hero ? 'hpbar--hero' : '',
    mini ? 'hpbar--mini' : '',
    ratio <= 1 / 3 ? 'is-low' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={className}>
      <span className="hpbar__name">{name}</span>
      <span className="hpbar__track">
        <span className="hpbar__fill" style={{ width: `${ratio * 100}%` }} />
        <span className="hpbar__value">
          {hp}
          {mini ? null : <em>/{max}</em>}
        </span>
      </span>
    </span>
  );
}
